import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function Payments({ role, staffId }) {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [totals, setTotals] = useState({ total: 0, week: 0, month: 0 })

  useEffect(() => { fetchPayments() }, [filter])

  async function fetchPayments() {
    setLoading(true)
    let query = supabase
      .from('appointments')
      .select(`*, services (name, price), profiles!appointments_customer_id_fkey (full_name), staff (full_name)`)
      .eq('status', 'completed')
      .order('appointment_date', { ascending: false })

    if (role === 'provider' && staffId) query = query.eq('staff_id', staffId)

    const today = new Date()
    if (filter === 'today') {
      query = query.eq('appointment_date', today.toISOString().split('T')[0])
    } else if (filter === 'week') {
      const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7)
      query = query.gte('appointment_date', weekAgo.toISOString().split('T')[0])
    } else if (filter === 'month') {
      const monthAgo = new Date(); monthAgo.setMonth(monthAgo.getMonth() - 1)
      query = query.gte('appointment_date', monthAgo.toISOString().split('T')[0])
    }

    const { data } = await query
    if (data) {
      setPayments(data)
      const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7)
      const monthAgo = new Date(); monthAgo.setMonth(monthAgo.getMonth() - 1)
      setTotals({
        total: data.reduce((s, p) => s + parseFloat(p.total_price || 0), 0),
        week: data.filter(p => p.appointment_date >= weekAgo.toISOString().split('T')[0])
          .reduce((s, p) => s + parseFloat(p.total_price || 0), 0),
        month: data.filter(p => p.appointment_date >= monthAgo.toISOString().split('T')[0])
          .reduce((s, p) => s + parseFloat(p.total_price || 0), 0),
      })
    }
    setLoading(false)
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Payments</h1>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>${totals.total.toFixed(0)}</div>
          <div style={styles.statLabel}>Total Revenue</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>${totals.month.toFixed(0)}</div>
          <div style={styles.statLabel}>This Month</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>${totals.week.toFixed(0)}</div>
          <div style={styles.statLabel}>This Week</div>
        </div>
      </div>

      <div style={styles.filters}>
        {['all', 'today', 'week', 'month'].map(f => (
          <button
            key={f}
            style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}
            onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <div style={styles.empty}>Loading...</div> : (
        <div style={styles.paymentList}>
          {payments.length === 0 ? (
            <div style={styles.empty}>No payments found</div>
          ) : payments.map(payment => (
            <div key={payment.id} style={styles.paymentRow}>
              <div style={styles.paymentInfo}>
                <div style={styles.paymentClient}>{payment.profiles?.full_name}</div>
                <div style={styles.paymentService}>{payment.services?.name}</div>
                {role !== 'provider' && (
                  <div style={styles.paymentStaff}>✂️ {payment.staff?.full_name}</div>
                )}
              </div>
              <div style={styles.paymentRight}>
                <div style={styles.paymentDate}>{payment.appointment_date}</div>
                <div style={styles.paymentAmount}>${payment.total_price}</div>
                {payment.tip_amount > 0 && (
                  <div style={styles.paymentTip}>+${payment.tip_amount} tip</div>
                )}
                {payment.payment_method && (
                  <div style={styles.paymentMethod}>{payment.payment_method}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { padding: '32px', maxWidth: '1200px' },
  title: {
    fontSize: '28px', fontWeight: '600', color: '#D4AF77',
    letterSpacing: '2px', textTransform: 'uppercase',
    fontFamily: 'Cormorant Garamond, serif', marginBottom: '24px',
  },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' },
  statCard: { backgroundColor: '#111', border: '1px solid #1a1a1a', borderRadius: '2px', padding: '20px' },
  statValue: {
    fontSize: '32px', fontWeight: '300', color: '#D4AF77',
    fontFamily: 'Cormorant Garamond, serif', marginBottom: '4px',
  },
  statLabel: { fontSize: '10px', color: '#555', letterSpacing: '1px', textTransform: 'uppercase' },
  filters: { display: 'flex', gap: '8px', marginBottom: '20px' },
  filterBtn: {
    padding: '8px 20px', background: 'none', border: '1px solid #222',
    color: '#555', fontSize: '11px', fontWeight: '600', letterSpacing: '1.5px',
    textTransform: 'uppercase', cursor: 'pointer', borderRadius: '2px',
    fontFamily: 'Montserrat, sans-serif',
  },
  filterBtnActive: { backgroundColor: '#D4AF77', borderColor: '#D4AF77', color: '#000' },
  paymentList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  paymentRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: '#111', border: '1px solid #1a1a1a',
    borderRadius: '2px', padding: '16px 20px',
  },
  paymentInfo: {},
  paymentClient: { fontSize: '15px', fontWeight: '600', color: '#F5F0E8', marginBottom: '2px' },
  paymentService: { fontSize: '13px', color: '#666', marginBottom: '2px' },
  paymentStaff: { fontSize: '12px', color: '#444' },
  paymentRight: { textAlign: 'right' },
  paymentDate: { fontSize: '12px', color: '#555', marginBottom: '4px' },
  paymentAmount: { fontSize: '18px', fontWeight: '600', color: '#D4AF77', marginBottom: '2px' },
  paymentTip: { fontSize: '11px', color: '#4A754A' },
  paymentMethod: { fontSize: '11px', color: '#555', letterSpacing: '0.5px', textTransform: 'capitalize' },
  empty: { color: '#444', fontSize: '14px', padding: '24px', textAlign: 'center' },
}